import { ApplicationController } from "../../support/application-controller";

/**
 * Stimulus controller for inline quick-add of HubSpot subscription mapping.
 * Shows a small modal/popover to enter a HubSpot subscription Record ID,
 * creates the ExternalMapping, then optionally syncs missing contact/deal mappings.
 */
export default class extends ApplicationController {
    static targets = [
        "modal",
        "input",
        "submitButton",
        "statusMessage",
        "syncSection",
        "syncButton"
    ];

    connect() {
        this.subscriptionId = this.data.get("subscriptionId");
        this.createMappingUrl = this.data.get("createMappingUrl");
        this.analyzeUrl = this.data.get("analyzeUrl");
        this.createMappingsUrl = this.data.get("createMappingsUrl");
    }

    openModal(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.hasModalTarget) {
            this.modalTarget.style.display = "flex";
            document.body.style.overflow = "hidden";
            if (this.hasInputTarget) {
                this.inputTarget.value = "";
                this.inputTarget.focus();
            }
            this.clearStatus();
            this.showSyncSection(false);
        }
    }

    closeModal(event) {
        if (event) event.preventDefault();
        if (this.hasModalTarget) {
            this.modalTarget.style.display = "none";
            document.body.style.overflow = "";
        }
    }

    closeOnBackdrop(event) {
        if (event.target === this.modalTarget) {
            this.closeModal(event);
        }
    }

    /**
     * Creates the ExternalMapping linking the TractIQ subscription to the HubSpot subscription Record ID.
     */
    createMapping(event) {
        var self = this;
        event.preventDefault();

        var hubspotId = this.hasInputTarget ? this.inputTarget.value.trim() : "";
        if (!hubspotId) {
            this.showStatus("Please enter a HubSpot subscription Record ID.", "error");
            return;
        }

        this.disableSubmit("Creating...");

        fetch(this.createMappingUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this.getCSRFToken()
            },
            credentials: "same-origin",
            body: JSON.stringify({
                external_mapping: {
                    local_source_type: "Subscription",
                    local_source_id: this.subscriptionId,
                    external_source_type: "Hubspot",
                    external_source_id: hubspotId
                }
            })
        })
        .then(function(response) {
            return response.json().then(function(data) {
                return { ok: response.ok, data: data };
            });
        })
        .then(function(result) {
            if (result.ok) {
                self.showStatus("HubSpot subscription mapping created successfully!", "success");
                self.enableSubmit();
                // Show option to sync missing contact/deal mappings
                if (self.analyzeUrl && self.createMappingsUrl) {
                    self.showSyncSection(true);
                } else {
                    // Reload after short delay
                    setTimeout(function() { window.location.reload(); }, 1000);
                }
            } else {
                var errorMsg = result.data.error || result.data.errors || "Failed to create mapping";
                self.showStatus(String(errorMsg), "error");
                self.enableSubmit();
            }
        })
        .catch(function(error) {
            self.showStatus("Error: " + error.message, "error");
            self.enableSubmit();
        });
    }

    /**
     * Syncs missing contact/deal mappings via the HubSpot missing mappings service,
     * then reloads the page.
     */
    syncMissingMappings(event) {
        var self = this;
        event.preventDefault();

        if (this.hasSyncButtonTarget) {
            this.syncButtonTarget.disabled = true;
            this.syncButtonTarget.textContent = "Syncing...";
        }

        var hubspotId = this.hasInputTarget ? this.inputTarget.value.trim() : "";

        // First analyze what's available
        var analyzeUrlObj = new URL(this.analyzeUrl, window.location.origin);
        if (hubspotId) {
            analyzeUrlObj.searchParams.set("hubspot_subscription_id", hubspotId);
        }

        fetch(analyzeUrlObj.toString(), {
            method: "GET",
            headers: { "Accept": "application/json", "X-CSRF-Token": this.getCSRFToken() },
            credentials: "same-origin"
        })
        .then(function(response) { return response.json(); })
        .then(function(data) {
            if (!data.success) {
                self.showStatus("Analyze failed: " + (data.error || "Unknown error"), "error");
                return;
            }

            // Collect available mapping types
            var types = [];
            if (data.available) {
                if (data.available.contact && data.available.contact.available) types.push("contact");
                if (data.available.deal && data.available.deal.available) types.push("user_deal");
                if (data.available.deal && data.available.deal.available && data.existing && data.existing.org_deal !== null && !data.existing.org_deal) {
                    types.push("org_deal");
                }
            }

            if (types.length === 0) {
                self.showStatus("No additional mappings available to sync. Reloading...", "success");
                setTimeout(function() { window.location.reload(); }, 1000);
                return;
            }

            // Create the mappings
            return fetch(self.createMappingsUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-CSRF-Token": self.getCSRFToken()
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    hubspot_subscription_id: hubspotId,
                    mapping_types: types
                })
            })
            .then(function(response) { return response.json(); })
            .then(function(result) {
                if (result.success) {
                    self.showStatus("All mappings synced! Reloading...", "success");
                } else {
                    self.showStatus("Sync partially failed: " + (result.error || "Unknown error"), "error");
                }
                setTimeout(function() { window.location.reload(); }, 1000);
            });
        })
        .catch(function(error) {
            self.showStatus("Sync error: " + error.message, "error");
        });
    }

    handleKeydown(event) {
        if (event.key === "Enter") {
            this.createMapping(event);
        }
    }

    skipSync(event) {
        event.preventDefault();
        window.location.reload();
    }

    // Private helpers

    showStatus(message, type) {
        if (this.hasStatusMessageTarget) {
            this.statusMessageTarget.textContent = message;
            this.statusMessageTarget.style.display = "block";
            this.statusMessageTarget.style.color = type === "error" ? "#dc3545" : "#28a745";
        }
    }

    clearStatus() {
        if (this.hasStatusMessageTarget) {
            this.statusMessageTarget.style.display = "none";
            this.statusMessageTarget.textContent = "";
        }
    }

    showSyncSection(visible) {
        if (this.hasSyncSectionTarget) {
            this.syncSectionTarget.style.display = visible ? "block" : "none";
        }
    }

    disableSubmit(text) {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true;
            this.submitButtonTarget.textContent = text || "Creating...";
        }
    }

    enableSubmit() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = false;
            this.submitButtonTarget.textContent = "Create Mapping";
        }
    }

    getCSRFToken() {
        var meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute("content") : "";
    }
}
